// Effects preferences (kept in the browser) and the live numbers the effects director reports to the HUD.

import { create } from "zustand";

/** cinematic: everything (tilted camera, shake, flashes, film look); simple: explosions and fire only; off: none. */
export type FxLevel = "cinematic" | "simple" | "off";
export const FX_LEVELS: { key: FxLevel; label: string; note: string }[] = [
  { key: "cinematic", label: "Cinematic", note: "Explosions, fire and smoke, camera shake and tilt, film look" },
  { key: "simple", label: "Simple", note: "Explosions, fire and smoke; no shake, flashes or tilt" },
  { key: "off", label: "Off", note: "A plain atlas, silent" },
];

const KEY = "fx-prefs";

interface Saved { level?: FxLevel; sound?: boolean; volume?: number }

function load(): Saved {
  try {
    const x = JSON.parse(localStorage.getItem(KEY) ?? "{}") as Saved;
    return x && typeof x === "object" ? x : {};
  } catch {
    return {};
  }
}

function save(s: Saved) {
  try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* private mode: preferences last for the visit */ }
}

const media = (q: string) => typeof window !== "undefined" && !!window.matchMedia?.(q).matches;

/** ?fx=off|simple|cinematic overrides the saved level for this visit (handy for slow machines and tests). */
function urlLevel(): FxLevel | null {
  if (typeof window === "undefined") return null;
  const v = new URLSearchParams(window.location.search).get("fx");
  return v === "off" || v === "simple" || v === "cinematic" ? v : null;
}

export interface FxStats {
  battles: number; // battles of the replayed war on the map so far
  burning: number; // places burning on screen
}

interface FxState {
  level: FxLevel;
  sound: boolean;
  volume: number;
  reducedMotion: boolean;
  audioBlocked: boolean; // sound is on but the browser waits for a click before it may play
  stats: FxStats;
  setLevel(l: FxLevel): void;
  setSound(on: boolean): void;
  setVolume(v: number): void;
  setAudioBlocked(b: boolean): void;
  setStats(s: FxStats): void;
}

const saved = load();
const reducedMotion = media("(prefers-reduced-motion: reduce)");

export const useFx = create<FxState>((set, get) => ({
  level: urlLevel() ?? saved.level ?? (reducedMotion ? "simple" : "cinematic"),
  sound: saved.sound ?? true,
  volume: typeof saved.volume === "number" ? Math.min(1, Math.max(0, saved.volume)) : 0.7,
  reducedMotion,
  audioBlocked: false,
  stats: { battles: 0, burning: 0 },
  setLevel: (level) => { set({ level }); save({ level, sound: get().sound, volume: get().volume }); },
  setSound: (sound) => { set({ sound }); save({ level: get().level, sound, volume: get().volume }); },
  setVolume: (volume) => { set({ volume }); save({ level: get().level, sound: get().sound, volume }); },
  setAudioBlocked: (audioBlocked) => { if (get().audioBlocked !== audioBlocked) set({ audioBlocked }); },
  setStats: (stats) => {
    const s = get().stats;
    if (s.battles !== stats.battles || s.burning !== stats.burning) set({ stats });
  },
}));

/** Camera shake, screen flashes and the tilted camera need the cinematic level and no reduced-motion request. */
export const motionFx = (s: Pick<FxState, "level" | "reducedMotion">) => s.level === "cinematic" && !s.reducedMotion;
